// WebSocket client for the clinic AI agent ("Sissi Clinic").
// Ported almost verbatim from the RN coach app (app/services/coachSocket.ts):
// only the URL (/ws/clinic-agent/), the env source, and the assistant_message
// payload (+ proposals/done) differ. Keep the run_id dedup, backoff, connect
// timeout and token-refresh dance intact — they are battle-tested.

import { Proposal, ProposalResolveResult } from '../models/clinicAgent';

export type ClinicSocketState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type AssistantMessagePayload = {
  text: string;
  proposals: Proposal[];
  run_id: number;
  done: boolean;
};

export type ProactiveMessagePayload = {
  text: string;
  trigger: string;
  chat_id: number;
};

export type AgentStatusPayload = {
  label: string;
  run_id: number | null;
};

export type ClinicMessagePayload = {
  type: 'message';
  text: string;
  patient_id?: number;
  shadow_context?: Record<string, unknown>;
  // Inlined attachment as a base64 data URL ("data:<mime>;base64,…") plus its
  // mime. file_name is sent for PDFs so the model can reference the document.
  file_data?: string;
  file_type?: string;
  file_name?: string;
};

// Proposals are now resolved over REST (POST /api/clinic/proposals/resolve/),
// so the only thing the client sends over the socket is chat messages.
export type ClinicSendPayload = ClinicMessagePayload;

// Acknowledges a resolve; `pending` is the count buffered for the agent's next
// turn. Only relevant if a decision is ever sent over the socket.
export type ProposalAckPayload = {
  pending: number;
};

// Broadcast to every open tab/device of the clinician when a proposal resolves
// (carries the same enriched result objects as the REST resolve response).
export type ProposalResolvedPayload = {
  results: ProposalResolveResult[];
};

export type ClinicSocketEvents = {
  onOpen?: () => void;
  onTyping?: () => void;
  onAssistantMessage?: (m: AssistantMessagePayload) => void;
  onProactiveMessage?: (m: ProactiveMessagePayload) => void;
  onAgentStatus?: (m: AgentStatusPayload) => void;
  onProposalAck?: (m: ProposalAckPayload) => void;
  onProposalResolved?: (m: ProposalResolvedPayload) => void;
  onClose?: (code: number) => void;
  onUnauthorized?: () => void;
  onStateChange?: (s: ClinicSocketState) => void;
  refreshToken?: () => Promise<boolean>;
};

const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const UNAUTHORIZED_CLOSE_CODE = 4401;
const CONNECT_TIMEOUT_MS = 10000;

function buildWsUrl(token: string): string {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';
  const base = apiUrl
    .replace(/^http:\/\//i, 'ws://')
    .replace(/^https:\/\//i, 'wss://')
    .replace(/\/api\/?$/i, '')
    .replace(/\/$/, '');
  return `${base}/ws/clinic-agent/?token=${encodeURIComponent(token)}`;
}

export class ClinicAgentSocket {
  private ws: WebSocket | null = null;
  private manualClose = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private state: ClinicSocketState = 'closed';
  private hadOpenedThisAttempt = false;
  private refreshInFlight = false;
  private failedAttemptsWithoutOpen = 0;

  constructor(
    private getToken: () => string | undefined,
    private events: ClinicSocketEvents,
  ) {}

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get currentState(): ClinicSocketState {
    return this.state;
  }

  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      return;
    }

    const token = this.getToken();
    if (!token) {
      this.setState('closed');
      return;
    }

    this.manualClose = false;
    this.hadOpenedThisAttempt = false;
    this.setState(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');

    try {
      this.ws = new WebSocket(buildWsUrl(token));
    } catch (err) {
      console.error('ClinicAgentSocket: failed to construct WebSocket', err);
      this.scheduleReconnect();
      return;
    }

    this.armConnectTimeout();

    this.ws.onopen = () => {
      this.clearConnectTimeout();
      this.reconnectAttempt = 0;
      this.failedAttemptsWithoutOpen = 0;
      this.hadOpenedThisAttempt = true;
      this.setState('open');
      this.events.onOpen?.();
    };

    this.ws.onmessage = (event) => {
      this.handleIncoming(event.data);
    };

    this.ws.onerror = (event) => {
      console.warn('ClinicAgentSocket: error', event);
    };

    this.ws.onclose = (event) => {
      const code = event.code;
      const opened = this.hadOpenedThisAttempt;
      this.clearConnectTimeout();
      this.events.onClose?.(code);
      this.ws = null;

      if (code === UNAUTHORIZED_CLOSE_CODE) {
        this.manualClose = true;
        this.setState('closed');
        this.events.onUnauthorized?.();
        return;
      }

      if (this.manualClose) {
        this.setState('closed');
        return;
      }

      if (!opened) {
        this.failedAttemptsWithoutOpen += 1;
        if (this.failedAttemptsWithoutOpen === 1 && this.events.refreshToken) {
          this.tryRefreshAndReconnect();
          return;
        }
      } else {
        this.failedAttemptsWithoutOpen = 0;
      }

      this.scheduleReconnect();
    };
  }

  close(): void {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearConnectTimeout();
    this.reconnectAttempt = 0;
    this.teardownSocket();
    this.setState('closed');
  }

  forceReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearConnectTimeout();
    this.reconnectAttempt = 0;
    this.manualClose = false;
    this.teardownSocket();
    this.connect();
  }

  private teardownSocket(): void {
    if (!this.ws) return;
    try {
      this.ws.onopen = null as any;
      this.ws.onmessage = null as any;
      this.ws.onerror = null as any;
      this.ws.onclose = null as any;
      this.ws.close();
    } catch (err) {
      console.warn('ClinicAgentSocket: error closing', err);
    }
    this.ws = null;
  }

  private armConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
        console.warn('ClinicAgentSocket: connect timeout, forcing reconnect');
        this.teardownSocket();
        this.scheduleReconnect();
      }
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  send(payload: ClinicSendPayload): boolean {
    if (!this.isOpen || !this.ws) return false;
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.error('ClinicAgentSocket: send failed', err);
      return false;
    }
  }

  private handleIncoming(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let data: any;
    try {
      data = JSON.parse(raw);
    } catch (err) {
      console.warn('ClinicAgentSocket: invalid JSON frame', err);
      return;
    }

    switch (data?.type) {
      case 'typing':
        this.events.onTyping?.();
        break;
      case 'assistant_message': {
        const text = String(data.text ?? '');
        const proposals = Array.isArray(data.proposals) ? (data.proposals as Proposal[]) : [];
        this.events.onAssistantMessage?.({
          text,
          proposals,
          run_id: Number(data.run_id),
          done: Boolean(data.done),
        });
        break;
      }
      case 'agent_status': {
        const label = String(data.label ?? '');
        const rawRunId = data.run_id;
        const run_id = rawRunId === null || rawRunId === undefined ? null : Number(rawRunId);
        this.events.onAgentStatus?.({ label, run_id });
        break;
      }
      case 'proactive_message': {
        const text = String(data.text ?? '');
        this.events.onProactiveMessage?.({
          text,
          trigger: String(data.trigger ?? ''),
          chat_id: Number(data.chat_id),
        });
        break;
      }
      case 'proposal_ack': {
        this.events.onProposalAck?.({ pending: Number(data.pending) || 0 });
        break;
      }
      case 'proposal_resolved': {
        const results = Array.isArray(data.results) ? (data.results as ProposalResolveResult[]) : [];
        this.events.onProposalResolved?.({ results });
        break;
      }
      default:
        console.debug('ClinicAgentSocket: unknown type', data?.type, data);
    }
  }

  private async tryRefreshAndReconnect(): Promise<void> {
    if (this.refreshInFlight || !this.events.refreshToken) {
      this.scheduleReconnect();
      return;
    }
    this.refreshInFlight = true;
    this.setState('reconnecting');
    try {
      const ok = await this.events.refreshToken();
      if (this.manualClose) return;
      if (ok) {
        this.reconnectAttempt = 0;
        this.connect();
      } else {
        this.scheduleReconnect();
      }
    } catch (err) {
      console.warn('ClinicAgentSocket: refreshToken threw', err);
      if (!this.manualClose) this.scheduleReconnect();
    } finally {
      this.refreshInFlight = false;
    }
  }

  private scheduleReconnect(): void {
    if (this.manualClose) return;
    const delay = BACKOFF_STEPS_MS[Math.min(this.reconnectAttempt, BACKOFF_STEPS_MS.length - 1)];
    this.reconnectAttempt += 1;
    this.setState('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private setState(next: ClinicSocketState): void {
    if (this.state === next) return;
    this.state = next;
    this.events.onStateChange?.(next);
  }
}
